import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { useAppContext } from "../../AppContext";
import orderRoute from "../../services/orderRoute";

const daysOfWeek = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const DEFAULT_TIME = "10:00";

// Build the editable row state from the current cut-off list (falls back to defaults).
const buildRows = (current = []) =>
  daysOfWeek.reduce((acc, day) => {
    const existing = current.find((c) => c?.day?.toLowerCase() === day);
    acc[day] = {
      checked: false,
      cutOffTime: existing?.cutOffTime || DEFAULT_TIME,
      startDate: existing?.startDate || "",
      endDate: existing?.endDate || "",
    };
    return acc;
  }, {});

const SetCutOffTimeModal = ({ show, onHide, currentCutOffTimes = [], onSuccess }) => {
  const { showToast } = useAppContext();
  const { updateCutOffTimes } = orderRoute();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(() => buildRows(currentCutOffTimes));

  // Re-seed the form whenever the modal is opened with the latest values.
  useEffect(() => {
    if (show) setRows(buildRows(currentCutOffTimes));
  }, [show, currentCutOffTimes]);

  const updateRow = (day, field, value) => {
    setRows((prev) => ({ ...prev, [day]: { ...prev[day], [field]: value } }));
  };

  const handleClose = () => {
    setRows(buildRows(currentCutOffTimes));
    onHide();
  };

  const handleSave = () => {
    const selectedDays = daysOfWeek.filter((day) => rows[day].checked);

    if (selectedDays.length === 0) {
      showToast({ toastText: "Please select at least one day to update", isSuccess: false });
      return;
    }

    const missingTime = selectedDays.find((day) => !rows[day].cutOffTime);
    if (missingTime) {
      showToast({ toastText: "Please set a time for each selected day", isSuccess: false });
      return;
    }

    // Only the checked days are sent for update.
    const body = selectedDays.map((day) => ({
      day,
      cutOffTime: rows[day].cutOffTime,
      startDate: rows[day].startDate || undefined,
      endDate: rows[day].endDate || undefined,
    }));

    updateCutOffTimes(body, setLoading, showToast, () => {
      if (onSuccess) onSuccess();
      handleClose();
    });
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ color: "#344054", fontWeight: "600" }}>
          Update Cut-Off Time
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 pt-2 pb-4">
        <p style={{ color: "#667085", fontSize: "14px" }}>
          Select days to update, then set the cut-off time. Date range optional.
        </p>

        <Row className="mb-2 mt-3" style={{ fontWeight: "600", color: "#344054", fontSize: "13px" }}>
          <Col xs={1}></Col>
          <Col xs={2}>Day</Col>
          <Col xs={3}>Time</Col>
          <Col xs={3}>Start Date</Col>
          <Col xs={3}>End Date</Col>
        </Row>

        {daysOfWeek.map((day) => {
          const row = rows[day];
          return (
            <Row key={day} className="align-items-center mb-3">
              <Col xs={1}>
                <Form.Check
                  type="checkbox"
                  id={`cutoff-${day}`}
                  checked={row.checked}
                  onChange={(e) => updateRow(day, "checked", e.target.checked)}
                />
              </Col>
              <Col xs={2}>
                <span style={{ fontWeight: "500", color: "#344054" }}>
                  {day.charAt(0).toUpperCase() + day.slice(1)}
                </span>
              </Col>
              <Col xs={3}>
                <Form.Control
                  type="time"
                  value={row.cutOffTime}
                  disabled={!row.checked}
                  onChange={(e) => updateRow(day, "cutOffTime", e.target.value)}
                />
              </Col>
              <Col xs={3}>
                <Form.Control
                  type="date"
                  value={row.startDate}
                  disabled={!row.checked}
                  onChange={(e) => updateRow(day, "startDate", e.target.value)}
                />
              </Col>
              <Col xs={3}>
                <Form.Control
                  type="date"
                  value={row.endDate}
                  disabled={!row.checked}
                  onChange={(e) => updateRow(day, "endDate", e.target.value)}
                />
              </Col>
            </Row>
          );
        })}
      </Modal.Body>
      <Modal.Footer className="border-0 px-4 pb-4">
        <Button variant="outline-secondary" onClick={handleClose} className="px-4">
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading} className="px-4">
          {loading ? <Spinner animation="border" size="sm" /> : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SetCutOffTimeModal;
